<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalTable v-if="modalShowTable" @closeModal="closeModal" :table="selectedTable" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Retur Penjualan" />
      <div
        class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500 dark:text-dark-500">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton v-if="role_id !== 'null'" to_url="#" button_text="+ Tambah Retur Penjualan"
            button_class="text-base py-2 font-medium" @click="showModal" />
          <InputSelect v-model="selectedCity" id="city-select"
            :options="optionCity" placeholder="Pilih kota" optionLabel="name" optionValue="name" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>SO</TableHeadCell>
                    <TableHeadCell>Customer</TableHeadCell>
                    <TableHeadCell>Kota</TableHeadCell>
                    <TableHeadCell>Tanggal</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allSalesReturns.length == 0">
                    <TableCell colspan="7" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(salesReturn, index) in allSalesReturns" :key="salesReturn.id" :index="index"
                    isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>
                      <div class="space-y-1">
                        <div class="font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[180px]">
                          {{ salesReturn?.sales_order?.code || '-' }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                          {{ formatTime(salesReturn.createdAt) }}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div class="space-y-1">
                        <div class="font-medium text-gray-900 dark:text-gray-100">
                          {{ salesReturn?.sales_order?.customer?.fullname || '-' }}
                        </div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">
                          {{ salesReturn?.sales_order?.customer?.phone || '-' }}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div class="text-gray-700 dark:text-gray-300">
                        {{ salesReturn?.sales_order?.customer?.city || '-' }}
                      </div>
                    </TableCell>
                    <TableCell>{{ formatTime(salesReturn.createdAt) }}</TableCell>
                    <TableCell>
                      <span :class="{
                        'bg-success dark:bg-success text-light-50 dark:text-white': salesReturn.status === 'paid',
                        'bg-info dark:bg-info text-white dark:text-white': salesReturn.status === 'pending',
                        'bg-danger dark:bg-danger text-light-50 dark:text-white': salesReturn.status === 'canceled'
                      }" class="px-2 py-1 rounded-full text-sm font-medium">
                        {{ getStatusText(salesReturn.status) }}
                      </span>
                    </TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + salesReturn.id" @click="editSalesReturn(salesReturn)"
                        class="bg-warning hover:bg-hover-warning flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 text-light-900 group-hover:text-primary" />
                      </button>
                      <template v-if="salesReturn.status != 'paid'">
                        <button :id="'delete-data-' + salesReturn.id" @click="deleteSalesReturn(salesReturn)"
                          class="bg-danger hover:bg-hover-danger flex justify-center items-center mx-1 p-2 rounded-md group">
                          <TrashIcon class="h-4 w-4 text-white group-hover:text-light-50" />
                        </button>
                      </template>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
  import IndexView from "@/views/IndexView.vue";
  import PageTitle from "@/components/PageTitle.vue";
  import AddButton from "@/components/buttons/AddButton.vue";
  import ModalTable from "@/components/modals/ModalTable.vue";
  import ViewContainer from "@/components/ViewContainer.vue";
  import {
    TableComponent,
    TableHead,
    TableRow,
    TableHeadCell,
    TableBody,
    TableCell,
  } from "@/components/table";
  import { PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/solid";
  import { mapGetters, mapActions } from "vuex";
  import PaginationButtons from "@/components/PaginationButtons.vue";
  import { Button, ConfirmDialog, Toast } from "primevue";
  import { formatTime } from "@/helpers/formatTime";
  import InputSelect from "@/components/form/InputSelect.vue";
  import { kota } from "@/helpers/kota";

  export default {
    name: "SalesReturnView",
    components: {
      IndexView, PageTitle, AddButton, ModalTable, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, PaginationButtons, Toast, ConfirmDialog, Button, InputSelect
    },
    data() {
      return {
        role_id: localStorage.getItem("role_id"),
        modalShowTable: false,
        isBlur: false,
        selectedTable: null,
        currentPage: 1,
        totalPages: 1,
        selectedCity: null,
      }
    },
    computed: {
      ...mapGetters('sales_returns', ['allSalesReturns']),
      optionCity() {
        return [{ name: 'Semua Kabupaten/Kota' }, ...kota];
      }
    },
    created() {
      this.fetchSalesReturns();
    },
    watch: {
      currentPage: {
        immediate: true,
        handler(newVal) {
          this.fetchSalesReturns({ page: newVal });
        },
      },
      selectedCity: {
        immediate: true,
        handler(newVal) {
          const cityQuery = newVal === 'Semua Kabupaten/Kota' ? null : newVal;
          this.fetchSalesReturns({ page: 1, city: cityQuery });
          this.currentPage = 1;
        },
      },
    },
    methods: {
      formatTime,
      ...mapActions('sales_returns', ['deleteSalesReturnById']),
      async fetchSalesReturns(params = {}) {
        if (!params.page) params.page = this.currentPage;

        try {
          const { pagination } = await this.$store.dispatch('sales_returns/fetchSalesReturns', params);
          if (pagination) {
            this.totalPages = pagination.totalPages;
          }
        } catch (error) {
          console.log(error);
        }
      },
      getStatusText(status) {
        const statusMap = {
          'paid': 'Posted',
          'pending': 'Draft',
          'canceled': 'Void'
        };
        return statusMap[status] || status;
      },
      showModal() {
        this.$router.push('/sales-return/new');
        // this.modalShowTable = true;
        // this.isBlur = true;
        // this.selectedTable = null;
      },
      closeModal() {
        setTimeout(() => {
          this.modalShowTable = false;
          this.isBlur = false;
          this.fetchTables();
        }, 200);
      },
      editSalesReturn(data) {
        this.$router.push(`sales-return/edit/${data.id}`);
      },
      async deleteSalesReturn(table) {
        this.$confirm.require({
          group: 'headless',
          header: 'Apakah anda yakin ingin menghapus retur penjualan?',
          message: 'Silahkan konfirmasi sebelum diproses.',
          acceptLabel: 'Ya',
          rejectLabel: 'Tidak',
          accept: async () => {
            try {
              await this.deleteSalesReturnById(table.id);
              this.fetchSalesReturns();
              this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus retur penjualan', life: 3000 });
            }
            catch (error) {
              console.error('Gagal menghapus retur penjualan', error);
              this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus retur penjualan', life: 3000 });
            }
          },
          reject: () => {
            this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus retur penjualan', life: 3000 });
          }
        });
      }
    },
  }
</script>
